import { useEffect, useMemo, useState } from 'react';
import SimpleModal from '../SimpleModal';
import { fetchChannelsIndex, createChannel } from '../../api/channels';
import { createCategory, fetchCategories } from '../../api/categoriesApi';
import {
  buildCategoryOrderMap,
  categoryPickerOptionsOrdered,
  sortRowsByCategoryOrder,
} from '../../lib/channelSort';
import { useToast } from '../../context/ToastContext';
import { useUndoRedo } from '../../context/UndoRedoContext';
import { usePlaylist } from '../../context/PlaylistContext';
const NEW_CATEGORY_VALUE = '__NEW__';

function normalizeCategoryForPicker(cat: string | null | undefined): string {
  if (cat == null || cat === '') return 'UNCATEGORIZED';
  return String(cat).toUpperCase();
}

export default function CreateChannelModal({ show, onClose }: { show: boolean; onClose: () => void }) {
  const { showToast } = useToast();
  const { refreshEditor } = useUndoRedo();
  const { playlistId, playlistName } = usePlaylist();
  const [busy, setBusy] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categoryList, setCategoryList] = useState<string[]>([]);

  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [category, setCategory] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [logo, setLogo] = useState('');
  const [epgId, setEpgId] = useState('');
  const [channelNumber, setChannelNumber] = useState('');
  const [syncName, setSyncName] = useState(false);

  const displayPlaylistName = playlistName.trim() ? playlistName : 'Unknown';

  useEffect(() => {
    if (!show) return;
    if (!playlistId) return;
    setLoadingCategories(true);
    Promise.all([
      fetchChannelsIndex(playlistId),
      fetchCategories(playlistId).catch(() => []),
    ])
      .then(([channels, categories]) => {
        const ch = channels.map((c) => ({
          ...c,
          sort_order: c.sort_order !== undefined ? c.sort_order : c.channel_number,
        }));
        const categoryOrderMap = buildCategoryOrderMap(categories);
        const sorted = sortRowsByCategoryOrder(ch, categoryOrderMap);
        setCategoryList(categoryPickerOptionsOrdered(sorted, categories, normalizeCategoryForPicker));
      })
      .catch(() => setCategoryList([]))
      .finally(() => setLoadingCategories(false));
  }, [show, playlistId]);

  const categoryOptions = useMemo(() => [...categoryList, NEW_CATEGORY_VALUE], [categoryList]);

  async function onSave() {
    if (!playlistId) {
      showToast('First select a playlist from Playlist manager.', 'warning');
      return;
    }
    const n = name.trim();
    const u = url.trim();
    if (!n) {
      showToast('Enter channel name.', 'warning');
      return;
    }
    if (!u) {
      showToast('Enter stream URL.', 'warning');
      return;
    }

    setBusy(true);
    try {
      let finalCategory = category.trim();
      if (finalCategory === NEW_CATEGORY_VALUE) {
        const newCat = newCategory.trim();
        if (!newCat) {
          showToast('Enter new category name.', 'warning');
          return;
        }
        const createResult = await createCategory({ playlist_id: Number(playlistId), name: newCat });
        if (!createResult.success && !String(createResult.message || '').includes('already exists')) {
          showToast(createResult.message || 'Failed to create category', 'error');
          return;
        }
        finalCategory = newCat;
      }

      const payload = {
        playlist_id: Number(playlistId),
        name: n,
        url: u,
        logo: logo.trim() || null,
        epg_id: epgId.trim() || null,
        channel_number: Number(channelNumber) || 0,
        category: finalCategory || null,
        is_hidden: 0,
        sync_name: syncName ? 1 : 0,
      };

      const result = await createChannel(payload);
      if (!result.success) {
        showToast(result.message || 'Failed to create channel', 'error');
        return;
      }

      showToast(`Channel "${n}" has been created.`, 'success');
      onClose();
      await refreshEditor();
      setName('');
      setUrl('');
      setCategory('');
      setNewCategory('');
      setLogo('');
      setEpgId('');
      setChannelNumber('');
      setSyncName(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <SimpleModal
      show={show}
      onClose={onClose}
      title="Create new channel"
      size="lg"
      footer={
        <div className="d-flex justify-content-end gap-2 w-100">
          <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void onSave()} disabled={busy}>
            {busy ? <span className="spinner-border spinner-border-sm" /> : 'Create channel'}
          </button>
        </div>
      }
    >
      {!playlistId ? (
        <div className="alert alert-warning mb-0">First select a playlist in Playlist manager.</div>
      ) : (
        <>
          <div className="mb-3">
            <label className="form-label">Channel name *</label>
            <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. TVP1 HD" />
          </div>
          <div className="mb-3">
            <label className="form-label">Stream URL *</label>
            <input className="form-control" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="http://example.com/stream.m3u8" />
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Category</label>
              <select className="form-select" value={category} onChange={(e) => setCategory(e.target.value)} disabled={loadingCategories}>
                <option value="">-- Select category --</option>
                {categoryOptions.map((opt) =>
                  opt === NEW_CATEGORY_VALUE ? (
                    <option key={opt} value={opt}>
                      + Create new category
                    </option>
                  ) : (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ),
                )}
              </select>
            </div>
            {category === NEW_CATEGORY_VALUE && (
              <div className="col-md-6 mb-3">
                <label className="form-label">New category name</label>
                <input className="form-control" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} />
              </div>
            )}
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Logo (URL)</label>
              <input className="form-control" value={logo} onChange={(e) => setLogo(e.target.value)} placeholder="https://example.com/logo.png" />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">EPG ID</label>
              <input className="form-control" value={epgId} onChange={(e) => setEpgId(e.target.value)} placeholder="e.g. TVP1.pl" />
            </div>
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Channel number</label>
              <input className="form-control" type="number" value={channelNumber} onChange={(e) => setChannelNumber(e.target.value)} />
            </div>
            <div className="col-md-6 mb-3 d-flex align-items-end">
              <div className="form-check mb-2">
                <input className="form-check-input" type="checkbox" id="create-channel-sync" checked={syncName} onChange={(e) => setSyncName(e.target.checked)} />
                <label className="form-check-label" htmlFor="create-channel-sync">
                  Sync name with stream
                </label>
              </div>
            </div>
          </div>
          <div className="text-muted small">
            <i className="fas fa-info-circle me-1" />
            Playlist: <strong>{displayPlaylistName}</strong>
          </div>
        </>
      )}
    </SimpleModal>
  );
}
